import { useMemo } from 'react'
import { useAnnotationStats } from '../store/hooks'

export interface DatasetStats {
  total_annotated_images?: number
  total_unannotated_images?: number
  total_annotations?: number
  annotations_per_class?: Record<
    string,
    {
      name: string
      count: number
      balance: string
      zscore: number
    }
  >
}

export const useDatasetStats = (projectId: string, datasetId: string, datasets: string[]) => {
  // Normalize datasources for stable caching (sort + dedupe)
  const normalizedDatasets = useMemo(() => {
    return Array.from(new Set((datasets || []).filter(Boolean))).sort((a, b) => a.localeCompare(b))
  }, [datasets])

  // Single consolidated stats request for the provided datasources
  const { data: combined } = useAnnotationStats(projectId, datasetId, normalizedDatasets, {
    skip: !projectId || !datasetId,
  })

  // Build per-dataset stats from datasource_distributions in the combined response
  const result = useMemo(() => {
    const out: Record<string, DatasetStats> = {}

    const stats = combined?.statistics
    if (stats?.datasource_distributions) {
      const avgPerImage = stats.average_annotations_per_image || 1

      normalizedDatasets.forEach((ds) => {
        const dist = stats.datasource_distributions[ds]
        if (!dist) return

        const totalImages = dist.images || 0
        const totalAnnotations = dist.annotations || 0
        const estimatedAnnotatedImages = Math.min(
          totalImages,
          Math.max(0, Math.round(totalAnnotations / (avgPerImage || 1)))
        )
        const estimatedUnannotatedImages = Math.max(0, totalImages - estimatedAnnotatedImages)

        // Derive a per-dataset annotations_per_class using tag_ids presence (counts unknown per-dataset)
        const annotationsPerClass: NonNullable<DatasetStats['annotations_per_class']> = {}
        const tagIds = dist.tag_ids || []
        tagIds.forEach((tagId) => {
          const classInfo = stats.annotations_per_class?.[tagId]
          if (classInfo) {
            annotationsPerClass[tagId] = {
              name: classInfo.name,
              count: 0, // per-dataset counts not available; keep 0 to indicate unknown
              balance: classInfo.balance,
              zscore: classInfo.zscore,
            }
          }
        })

        out[ds] = {
          total_annotated_images: estimatedAnnotatedImages,
          total_unannotated_images: estimatedUnannotatedImages,
          total_annotations: totalAnnotations,
          annotations_per_class: annotationsPerClass,
        }
      })
    }

    // Aggregate entry for consumers that expect an overall snapshot
    if (combined?.statistics) {
      out.__aggregate__ = {
        total_annotated_images: combined.statistics.total_annotated_images || 0,
        total_unannotated_images: combined.statistics.total_unannotated_images || 0,
        total_annotations: combined.statistics.total_annotations || 0,
        annotations_per_class: combined.statistics.annotations_per_class || {},
      }
    }

    return out
  }, [combined, normalizedDatasets])

  return result
}
